import React, {useEffect, useMemo, useState} from 'react';
import {connect, useDispatch} from "react-redux";
import Loader from "./layout/loading/loader.js";
import {Formik, Field} from "formik";
import {date, object, string} from 'yup';
import {Box, Button, Step, StepLabel, Stepper, TextField, FormControl, InputLabel, Select, MenuItem, FormHelperText} from "@mui/material";
import {useParams} from "react-router-dom";
import PageTitle from "./layout/PageTitle.js";
import {TravelExplore} from "@mui/icons-material";
import CampaignOperateursField from "./campaigns/form/CampaignOperateursField.js";
import CampaignPatrimoineField from "./campaigns/form/CampaignPatrimoineField.js";
import SubmitButton from "./layout/forms/SubmitButton.js";
import {usersActions} from "../../modules/users/users.actions";
import {patrimoinesActions} from "../../modules/patrimoines/patrimoines.actions";
import {RRuleHelper} from "../../utils/RRuleHelper";
import {format} from "date-fns";
import {tourneesGraffitiService} from "../../modules/tourneesGraffiti/tourneesGraffiti.service";
import history from "../../utils/history";
import Constants from "../../utils/constants";
import CampaignFrequencyField from "./campaigns/form/CampaignFrequencyField.js";
import Grid from "@mui/material/Grid2";
import * as url from "node:url";

const validationSchema = object({
    libelle: string().required('Le libellé est requis'),
    dateDebut: date().required('La date de début est requise'),
    dateFin: date().required('La date de fin est requise'),
    client: string().required('Le client est requis'),
    patrimoine: string().required('Le patrimoine est requis'),
    chauffeur: string().required('Au moins un chauffeur doit être sélectionné'),
});

const steps = ["Détail de la tournée", "Sélection du patrimoine", "Sélection des opérateurs"];

function TourneeGraffitiNewOrEdit({...props}) {
    const dispatch = useDispatch();
    const params = useParams();

	const urlId = params.id ? params.id : null;
	const mode = urlId ? "edit" : "create";

    const [activeStep, setActiveStep] = useState(0);
    const [tour, setTour] = useState(null);

    useEffect(() => {
        if (!props.clients && !props.loading && !props.error) {
            dispatch(usersActions.fetchClients());
        }
    }, [dispatch, props.clients, props.loading, props.error]);

    useEffect(() => {
        if (!props.users && !props.loading && !props.error) {
            dispatch(usersActions.fetchUsers());
        }
    }, [dispatch, props.users, props.loading, props.error]);

    useEffect(() => {
        if (mode === 'edit' && urlId && !tour && !props.loading && !props.error) {
            tourneesGraffitiService.fetchTourneeGraffiti(urlId)
                .then(data => setTour(data))
                .catch(() => setTour(null));
        }
    }, [mode, urlId, tour, props.loading, props.error]);

    const getInitialValues = () => {
        const currentDate = new Date();
        const tomorrow = new Date();
        tomorrow.setDate(currentDate.getDate() + 1);
        const source = tour;
        return {
            id: source?.id ?? null,
            libelle: source?.libelle ?? '',
            dateDebut: source?.dateDebut ?? format(currentDate, 'yyyy-MM-dd'),
            dateFin: source?.dateFin ?? format(tomorrow, 'yyyy-MM-dd'),
            freq: RRuleHelper.FREQ[1].id,
            interval: 1,
            byDay: [],
            byMonthDay: [],
            byMonth: "",
            bySetPos: RRuleHelper.SETPOS[0].id,
            recurrenceType: RRuleHelper.RECURRINGTYPE.DAYOFMONTH,
            client: source?.client?.['@id'] ?? '',
            patrimoine: source?.patrimoine?.['@id'] ?? '',
            chauffeur: source?.chauffeur?.['@id'] ?? '',
            equipier: source?.equipier?.['@id'] ?? '',
        };
    };

    // Mémoïse les valeurs initiales pour éviter les réinitialisations Formik à chaque rendu
    const initialValues = useMemo(() => getInitialValues(), [tour]);

    // Charge les patrimoines si on arrive en mode édition et que le client est déjà connu
    useEffect(() => {
        if (initialValues?.client) {
            getPatrimoinesForClient(initialValues.client);
        }
    }, [initialValues?.client]);

    const getPatrimoinesForClient = (clientId) => {
        if (clientId.indexOf("api/") > -1) {
            const parts = clientId.split("/");
            clientId = parts[parts.length - 1];
        }
        dispatch(patrimoinesActions.fetchPatrimoinesForClient(clientId));
    };

    const getOperateurs = () => {
        if (props.users) {
            return props.users[0]?.operateurs;
        }
        return [];
    };

    const handleNextStep = () => setActiveStep((s) => s + 1);
    const handleBackStep = () => setActiveStep((s) => s - 1);

    const handleSubmit = async (values) => {
        const payload = {
            libelle: values.libelle,
            dateDebut: values.dateDebut,
            dateFin: values.dateFin,
            client: values.client,
            patrimoine: values.patrimoine,
            rrule: RRuleHelper.buildRRuleString(values),
            chauffeur: values.chauffeur,
            equipier: values.equipier || null,
        };
        if (mode === 'create') {
            await tourneesGraffitiService.createTourneeGraffiti(payload);
        } else {
            await tourneesGraffitiService.updateTourneeGraffiti(values.id, payload);
        }
        history.replace(Constants.APP_ROUTE_ADMIN_CAMPAIGNS);
    };


	console.log("LOADING ? " + props.loading)
    if (props.loading || (mode === 'edit' && !initialValues)) {
        return <Loader/>;
    }

    return (
        <>
            <PageTitle title={mode === "create" ? "Nouvelle tournée Graffiti" : "Modifier la tournée Graffiti"}
                       icon={<TravelExplore/>}/>
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                validationSchema={validationSchema}
                onSubmit={(values, {setSubmitting}) => {
                    setSubmitting(true);
                    handleSubmit(values).finally(() => setSubmitting(false));
                }}
            >
                {({values, errors, handleChange, handleBlur, handleSubmit, isSubmitting}) => (
                    <form onSubmit={handleSubmit} className={"mb-2"}>
                        <Stepper activeStep={activeStep} alternativeLabel sx={{mb: 2}}>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                        {activeStep === 0 && (
                            <div className={"sectionContainer"}>
                                {/* LIBELLE FIELD */}
                                <Grid container size={12} alignItems={"center"} className={"mt-2"}>
                                    <Grid size={3}>
                                        Libellé <span className="asterix">*</span>
                                    </Grid>
                                    <Grid size={9}>
                                        <Field type="text" name="libelle" value={values.libelle}>
                                            {({field, form}) => (
                                                <TextField
                                                    fullWidth
                                                    margin="normal"
                                                    variant="outlined"
                                                    name="libelle"
                                                    value={values.libelle}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={Boolean(errors.libelle)}
                                                    helperText={errors.libelle && String(errors.libelle)}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                </Grid>
                                {/* Fréquence (RRULE) */}
                                <CampaignFrequencyField values={values} errors={errors} handleChange={handleChange}
                                                        handleBlur={handleBlur}/>
                                {/* CLIENT FIELD */}
                                <Grid container size={12} alignItems={"center"} className={"mt-2"}>
                                    <Grid size={3}>
                                        Client <span className="asterix">*</span>
                                    </Grid>
                                    <Grid size={9}>
                                        <Field type="text" name="client" value={values.client}>
                                            {({field, form}) => (
                                                <FormControl fullWidth error={Boolean(errors.client)}>
                                                    <InputLabel id="label-client">Sélectionner le client :</InputLabel>
                                                    <Select
                                                        labelId="label-client"
                                                        label="Sélectionner le client :"
                                                        variant="outlined"
                                                        name="client"
                                                        value={values.client}
                                                        onChange={(e) => {
                                                            getPatrimoinesForClient(e.target.value);
                                                            handleChange(e);
                                                        }}
                                                    >
                                                        {props.clients && props.clients.map((c) => (
                                                            <MenuItem key={c.id} value={c['@id']}>
                                                                {c.libelle}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {errors.client && String(errors.client) && (
                                                        <FormHelperText error={true}>{errors.client}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            )}
                                        </Field>
                                    </Grid>
                                </Grid>
                            </div>
                        )}
                        {activeStep === 1 && props.patrimoines && (
                            <CampaignPatrimoineField values={values} errors={errors} handleChange={handleChange}
                                                     handleBlur={handleBlur} patrimoines={props.patrimoines}/>
                        )}
                        {activeStep === 2 && (
                            <CampaignOperateursField values={values} errors={errors} handleChange={handleChange}
                                                     handleBlur={handleBlur} operateurs={getOperateurs()}/>
                        )}
                        <Box sx={{display: 'flex', flexDirection: 'row', pt: 2}}>
                            <Button variant={"contained"} color={"primary"} disabled={activeStep === 0}
                                    onClick={handleBackStep} sx={{mr: 1}}>Retour</Button>
                            <Box sx={{flex: '1 1 auto'}}/>
                            {activeStep === steps.length - 1 ? (
                                <SubmitButton submitting={isSubmitting} label={mode === 'create' ? 'Créer' : 'Enregistrer'} color={"secondary"}/>
                            ) : (
                                <Button onClick={handleNextStep} variant={"contained"} color={"secondary"} sx={{color: 'white'}}>Suivant</Button>
                            )}
                        </Box>
                    </form>
                )}
            </Formik>
        </>
    );
}

const mapStateToProps = function (state) {
    return {
        clients: state.users.clients,
        patrimoines: state.patrimoines.patrimoines,
        users: state.users.users,
        loading: state.users.loading || state.campaigns.loading,
        error: state.users.error || state.patrimoines.error,
    };
};

export default connect(mapStateToProps)(TourneeGraffitiNewOrEdit);
